import { motion } from "framer-motion";
import { useState, useRef, useEffect, useCallback } from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, ChevronLeft, ChevronRight } from "lucide-react";
import { smoothScrollTo } from "../utils/smoothScroll";
import { useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/gmy7q88s_Photoroom_20260409_140507.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/k6v8bbgt_12637825-E895-4F2E-AAB9-F2796C680894.png";

export default function HeroBanner() {
  const navigate = useNavigate();
  const scrollToSection = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
    } else {
      smoothScrollTo(href);
    }
  };

  const navScrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollArrows = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 5);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  }, []);

  useEffect(() => {
    checkScrollArrows();
    window.addEventListener("resize", checkScrollArrows);
    return () => window.removeEventListener("resize", checkScrollArrows);
  }, [checkScrollArrows]);

  const scrollNav = (direction) => {
    const el = navScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 150, behavior: "auto" });
    setTimeout(checkScrollArrows, 100);
  };

  return (
    <section className="relative" data-testid="hero-banner">
      {/* Full-width Hero Image */}
      <div className="relative h-screen">
        <img
          src={HERO_IMAGE}
          alt="Draft beer taps"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />

        {/* Social Icons - Top Left */}
        <div className="absolute top-6 left-6 flex flex-col gap-3 z-10" data-testid="social-icons">
          <a
            href="https://www.facebook.com/TibsTaps/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-[#D4AF37] hover:text-black transition-all"
            data-testid="social-facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href="https://www.instagram.com/explore/locations/74093362/tibs-taps/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-[#D4AF37] hover:text-black transition-all"
            data-testid="social-instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
        </div>
        
        {/* Centered Logo & Content - shifted up to make room for banner */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pb-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-4"
          >
            <div className="bg-white rounded-full border-4 border-black w-52 h-52 sm:w-64 sm:h-64 md:w-80 md:h-80 flex items-center justify-center mx-auto">
              <img
                src={LOGO_URL}
                alt="Tibs Taps"
                className="w-36 sm:w-44 md:w-56 h-auto -translate-x-1"
                data-testid="hero-logo"
              />
            </div>
          </motion.div>

          {/* Decorative Line */}
          <div className="w-48 sm:w-64 h-[3px] bg-white/60 mb-3" />

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-4"
          >
            <div className="px-8 py-3 inline-block">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl text-white tracking-wide"
                style={{ fontFamily: "'Rye', cursive", textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}
                data-testid="hero-tagline"
              >
                Perfecting Every Pour
              </h2>
            </div>
          </motion.div>

          {/* Nav + Contact Banner - pinned to bottom of hero */}
          <div className="absolute bottom-0 left-0 right-0">
            {/* Navigation Links */}
            <nav className="bg-[#0A0A0A]/80 border-b border-[#27272A]" data-testid="main-nav">
              <div className="max-w-7xl mx-auto px-4 relative flex items-center">
                {showLeftArrow && (
                  <button
                    onClick={() => scrollNav(-1)}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-[#D4AF37]"
                    data-testid="nav-scroll-left"
                    aria-label="Scroll nav left"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <div
                  ref={navScrollRef}
                  onScroll={checkScrollArrows}
                  className="flex items-center gap-2 sm:gap-6 md:gap-10 py-3 overflow-x-auto flex-1 scrollbar-hide"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  {[
                    { name: "Services", href: "#services" },
                    { name: "About", href: "#why-us" },
                    { name: "Testimonials", href: "#testimonials" },
                    { name: "Book Now", href: "#booking" },
                    { name: "Blog", href: "#blog" },
                    { name: "Education", href: "#resources" },
                    { name: "Gallery", href: "#gallery" },
                    { name: "Contact", href: "#contact" },
                    { name: "Careers", href: "/careers" },
                  ].map((link) => (
                    <button
                      key={link.name}
                      onClick={() => scrollToSection(link.href)}
                      className="text-white hover:text-[#D4AF37] transition-colors text-sm sm:text-base font-medium whitespace-nowrap px-2"
                      data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </button>
                  ))}
                </div>
                {showRightArrow && (
                  <button
                    onClick={() => scrollNav(1)}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-[#D4AF37]"
                    data-testid="nav-scroll-right"
                    aria-label="Scroll nav right"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </nav>
            {/* Contact Banner */}
            <div className="bg-[#D4AF37] py-3" data-testid="contact-banner">
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-10">
                <a
                  href="mailto:tibstaps@gmail.com"
                  className="flex items-center gap-2 text-black hover:text-white transition-colors font-medium"
                  data-testid="banner-email"
                >
                  <Mail className="w-5 h-5" />
                  <span className="uppercase tracking-wide text-xs sm:text-sm">TIBSTAPS@GMAIL.COM</span>
                </a>
                <a
                  href="tel:603-548-9932"
                  className="flex items-center gap-2 text-black hover:text-white transition-colors font-medium text-lg sm:text-xl"
                  data-testid="banner-phone"
                >
                  <Phone className="w-5 h-5" />
                  <span>603-548-9932</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
